import { useState, useRef, useEffect } from "react";
import { useIntl } from "react-intl";
import { useSelector, useDispatch } from "react-redux";
import moment from 'moment';

import { Tooltip, Button, Toolbar } from '@gpstrackit/react-controls';
import { Icon } from "@mui/material";
import InfoIcon from '@mui/icons-material/Info';
import InputDate from 'Components/InputDate';
import { NotificationManager } from "Components/Notifications";

//Components
import DataViewInfiniteScroll from 'Components/DataViewInfiniteScroll';
import Details from 'Modules/accounts/.components/scheduledReports/.components/details';
import Contacts from 'Modules/accounts/.components/scheduledReports/.components/contacts';
import Filters from 'Components/Filters';

//Helpers
import { getDateFormatted, getMinDateLimitBehaviour } from 'Core/data/Helpers';
import { downloadFile } from 'Utils/helpers';

//Actions
import { scheduledReportsOpenModal, scheduledReportsContactsOpenModal, clearRedux, downloadReportFileRequest } from 'Redux/actions';

//Styles
import './index.scss';

const RANGE_MODE_ROWS = 50;
const REPORT_RETENTION_DAYS = 30;
const getRetentionMinDate = () => moment().subtract(REPORT_RETENTION_DAYS, 'days').startOf('day').toDate();

const ScheduleReports = (props) => {
    const { messages } = useIntl();
    const dispatch = useDispatch();
    const tableRef = useRef(null);
    const toolbarRef = useRef();
    const isInitialMountRef = useRef(true);

    //Variables

    const current_week = moment().startOf('week');
    const tempStartDate = new Date(current_week.toString());

    //Redux States
    const { loading, openModal, contactsOpenModal } = useSelector(state => state.scheduledReportsRedux);
    const lang = useSelector(state => state.commonRedux.language);
    const downloadReportFileResult = useSelector(state => state.generalRedux.downloadReportFileResult);

    const { company, height } = props;

    const [openSearch, setOpenSearch] = useState("");

    const [columns, setColumns] = useState([
        {
            key: "url",
            field: "outputUrl",
            title: "",
            show: true,
            width: 40,
            icon: true,
            render: (item) => {
                let url = null
                try {
                    url = JSON.parse(item?.outputUrl)
                    url = url[0]?.url
                } catch (error) { }
                return (
                    <>
                        {
                            url ?
                                <Tooltip title={messages['scheduled_download_report']}>
                                    <a href={"#"} onClick={(e) => {
                                        e.stopPropagation()
                                        e.preventDefault();
                                    }}>
                                        <Icon style={{ paddingTop: 3, cursor: 'pointer', color: '#000' }} onClick={() => url && dispatch(downloadReportFileRequest({id:url}))}>get_app</Icon>
                                    </a>
                                </Tooltip>
                                :
                                <Tooltip title={messages['scheduled_no_data_available']}>
                                    <Icon style={{ paddingTop: 3, opacity: '0.5', color: '#000' }}>get_app</Icon>
                                </Tooltip>


                        }
                    </>
                )
            }
        },
        {
            key: "params",
            field: "params",
            title: messages['scheduled_reports_params'],
            show: true,
            width: 80,
            code: "scheduled_reports_params",
            render: (item) => {
                return (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        <Tooltip title={messages['scheduled_show_params']}>
                            <InfoIcon style={{ cursor: 'pointer' }} onClick={() => dispatch(scheduledReportsOpenModal(true, item.params))} />
                        </Tooltip>
                    </div>
                )
            }
        },
        {
            key: "reportName",
            field: "reportName",
            title: messages['scheduled_reports_report_name'],
            sort: true,
            show: true,
            resize: true,
            width: 250,
            export: true,
            code: "scheduled_reports_report_name",
            render: (item) => <span>{item?.reportName}</span>
        },
        {
            key: "reportType",
            field: "reportType",
            sort: true,
            title: messages['scheduled_reports_report_type'],
            show: true,
            resize: true,
            width: 150,
            export: true,
            code: "scheduled_reports_report_type",
            render: (item) => <span>{item?.reportType}</span>
        },
        {
            key: "createdOn",
            field: "createdOn",
            sort: true,
            title: messages['scheduled_created_on'],
            show: true,
            resize: true,
            width: 200,
            export: true,
            functionString: `
                function formatDate(date) {
                    let dateToformat = moment(date)
                    let formatted = moment(dateToformat).format("YYYY/MM/DD HH:mm:ss")
                    return formatted;
                }
                return row?.createdOn ? formatDate(row?.createdOn) : ''
            `,
            code: "scheduled_created_on",
            render: (item) => <span>{item?.createdOn ? moment(item?.createdOn).utc().format('MMM DD YYYY h:mm:ss A') : ""}</span>
        },
        {
            key: "lastProcessUpdate",
            field: "lastProcessUpdate",
            title: messages['scheduled_last_process_update'],
            sort: true,
            show: true,
            resize: true,
            width: 200,
            export: true,
            functionString: `
                function formatDate(date) {
                    let dateToformat = moment(date)
                    let formatted = moment(dateToformat).format("YYYY/MM/DD HH:mm:ss")
                    return formatted;
                }
                return row?.lastProcessUpdate ? formatDate(row?.lastProcessUpdate) : ''
            `,
            code: "scheduled_last_process_update",
            render: (item) => <span>{item?.lastProcessUpdate ? moment(item?.lastProcessUpdate).utc().format('MMM DD YYYY h:mm:ss A') : ""}</span>
        },
        {
            key: "scheduledAt",
            field: "scheduledAt",
            sort: true,
            title: messages['scheduled_reports_scheduled_at'],
            show: true,
            resize: true,
            width: 200,
            export: true,
            functionString: `
                function formatDate(date) {
                    let dateToformat = moment(date)
                    let formatted = moment(dateToformat).format("YYYY/MM/DD HH:mm:ss")
                    return formatted;
                }
                return row?.scheduledAt ? formatDate(row?.scheduledAt) : ''
            `,
            code: "scheduled_reports_scheduled_at",
            render: (item) => <span>{item?.scheduledAt ? moment(item?.scheduledAt).utc().format('MMM DD YYYY h:mm:ss A') : ""}</span>
        },
        {
            key: "completedOn",
            field: "completedOn",
            sort: true,
            title: messages['scheduled_reports_completed_on'],
            show: true,
            resize: true,
            width: 200,
            export: true,
            functionString: `
                function formatDate(date) {
                    let dateToformat = moment(date)
                    let formatted = moment(dateToformat).format("YYYY/MM/DD HH:mm:ss")
                    return formatted;
                }
                return row?.completedOn ? formatDate(row?.completedOn) : ''
            `,
            code: "scheduled_reports_completed_on",
            render: (item) => <span>{item?.completedOn ? moment(item?.completedOn).utc().format('MMM DD YYYY h:mm:ss A') : ""}</span>
        },
        {
            key: "format",
            field: "format",
            title: messages['scheduled_reports_format'],
            sort: true,
            show: true,
            resize: true,
            width: 100,
            export: true,
            code: "scheduled_reports_format",
            render: (item) => <span>{item?.format}</span>
        },
        {
            key: "sendByEmail",
            field: "sendByEmail",
            title: messages['scheduled_reports_send_by_email'],
            sort: true,
            show: true,
            resize: true,
            width: 200,
            export: true,
            code: "scheduled_reports_send_by_email",
            functionString: `
                let lang = "${lang}";
                const translations = {
                    en: {
                        yes: "Yes",
                        no: "No",   
                    },
                    es: {
                        yes: "Si",
                        no: "No",
                    }
                };
                return row?.sendByEmail ? translations[lang].yes : translations[lang].no
            `,
            render: (item) => <span>{item?.sendByEmail ? messages['scheduled_reports_yes'] : messages['scheduled_reports_no']}</span>
        },
        {
            key: "emailsTo",
            field: "emailsTo",
            title: messages['scheduled_reports_emails'],
            show: true,
            resize: true,
            width: 100,
            export: true,
            code: "scheduled_reports_emails",
            functionString: `
                let emailsTo = []
                try {
                    emailsTo = JSON.parse(JSON.parse(row?.emailsTo))
                } catch(error){}
                return emailsTo?.length ? emailsTo : null
            `,
            render: (item) => {
                let emailsTo = []
                try {
                    emailsTo = JSON.parse(JSON.parse(item?.emailsTo))
                } catch (error) { }
                return (
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                        {emailsTo && emailsTo?.length
                            ? <Icon style={{ cursor: 'pointer' }} onClick={() => dispatch(scheduledReportsContactsOpenModal(true, emailsTo))}>people</Icon>
                            : <></>
                        }
                    </div>
                )
            }
        },
        {
            key: "reportStatus",
            field: "reportStatus",
            title: messages['scheduled_reports_status'],
            sort: true,
            show: true,
            resize: true,
            width: 150,
            export: true,
            code: "scheduled_reports_status",
            render: (item) => <span>{item?.reportStatus}</span>
        },
        {
            key: "error",
            field: "error",
            title: messages['scheduled_error'],
            sort: true,
            show: true,
            resize: true,
            width: 150,
            export: true,
            code: "scheduled_error",
            render: (item) => <span>{item?.error}</span>
        }
    ])

    const [startDate, setStartDate] = useState(tempStartDate);
    const [endDate, setEndDate] = useState(new Date());
    const [last, setLast] = useState({ id: '20', name: '20' });
    const [type, setType] = useState('last');

    const [beforeRangeDate, setBeforeRangeDate] = useState({
        start: tempStartDate,
        end: endDate
    })

    const [filters, setFilters] = useState({
        startDate: tempStartDate,
        endDate: endDate,
        openSearch: null,
    })

    const onChange = (value) => {
        const { start, end } = value;
        let endDate = end ? end.setHours(23, 59, 59) : null
        onChangeStartEndDate({
            start,
            end: endDate
        })
    };

    const onChangeOpenSearch = (value) => {
        setFilters(prev => ({ ...prev, openSearch: value }));
    }

    const onChangeStartEndDate = ({ start, end }) => {
        const newStart = start && new Date(start);
        const newEnd = end && new Date(end);

        let fltrs = filters;
        fltrs.startDate = newStart;
        fltrs.endDate = newEnd;
        // Set New Date Range
        if (start && end) {
            const newDateRange = {
                start,
                end,
            };
            setBeforeRangeDate(newDateRange)
        }
        setFilters({ ...fltrs });
    }

    const onCloseSelectDate = () => {
        const { start, end } = beforeRangeDate;
        if (start && end && !endDate) {
            let fltrs = filters;
            fltrs.startDate = start;
            fltrs.endDate = end;
            setFilters({ ...fltrs });
        }
    }

    const applyFilters = (paramType) => {
        const currentType = paramType ?? type;

        let currentNewConditions = tableRef?.current?.conditions
            ? [...tableRef.current.conditions]
            : [];
        let deleteAccountId = currentNewConditions?.findIndex((item) => item?.fields && item?.fields[0] === 'accountId');
        currentNewConditions && deleteAccountId > -1 && currentNewConditions.splice(deleteAccountId, 1);

        let deleteOpenSearch = currentNewConditions?.findIndex((item) => item?.fields && item?.fields[0] === 'reportName');
        currentNewConditions && deleteOpenSearch > -1 && currentNewConditions.splice(deleteOpenSearch, 1);

        const minDate = getRetentionMinDate();

        if (currentType !== 'last') {
            if (filters.startDate && filters.endDate) {
                const gteDate = moment.max(moment(filters.startDate), moment(minDate));

                currentNewConditions && currentNewConditions.push({
                    fields: ["accountId"],
                    terms: [company?.id],
                    exact: true,
                    range: {
                        fields: ["createdOn"],
                        GTE: gteDate.utc().format("YYYY-MM-DDTHH:mm:ss.sss") + "Z",
                        LTE: moment(moment(filters.endDate).format("YYYY-MM-DD") + " 23:59:00").utc().format("YYYY-MM-DDTHH:mm:ss.sss") + "Z"
                    }
                });
            } else {
                NotificationManager.warning(
                    messages['raw_data_tab_date_range'],
                    messages['raw_data_tab_raw_data_modal_title'],
                    5000,
                );
                return
            }
        } else
            currentNewConditions && currentNewConditions.push({
                fields: ["accountId"],
                terms: [company?.id],
                exact: true,
                range: {
                    fields: ["createdOn"],
                    GTE: moment(minDate).utc().format("YYYY-MM-DDTHH:mm:ss.sss") + "Z"
                }
            });

        if (filters.openSearch?.length) {
            currentNewConditions.push({
                fields: ["reportName", "reportType"],
                terms: filters.openSearch,
                exact: false
            })
        }

        //execute the account search
        tableRef?.current?.getData({ 
            newRows: currentType === 'last' ? parseInt(last.id, 10) : RANGE_MODE_ROWS, 
            newConditions: currentNewConditions 
        });

    }

    useEffect(() => {
        return () => {
            dispatch(clearRedux("SCHEDULED_REPORTS"))
        }
    }, [])

    useEffect(() => {
        // Skip on initial mount - let preferences restoration handle the first fetch
        // Only trigger applyFilters when user actively changes search
        if (isInitialMountRef.current) {
            isInitialMountRef.current = false;
            return;
        }
        
        if (openSearch || openSearch === "") {
            const timer = setTimeout(() => {
                applyFilters()
            }, 500)
            return () => clearTimeout(timer);
        }
    }, [openSearch])

    useEffect(() => {
        // Use functional updater to preserve preference-restored 'show' flags
        setColumns(prev => prev.map((item) => ({
            ...item,
            title: messages[item.code],
            render: item.key === "sendByEmail" ? (item) => {
                return <span>{item?.sendByEmail ? messages['scheduled_reports_yes'] : messages['scheduled_reports_no']}</span>
            } : item.render,
            functionString: item.key === "sendByEmail" ? `
                let lang = "${lang}";
                const translations = {
                    en: {
                        yes: "Yes",
                        no: "No",   
                    },
                    es: {
                        yes: "Si",
                        no: "No",
                    }
                };
                return row?.sendByEmail ? translations[lang].yes : translations[lang].no
            ` : item.functionString
        })))
    }, [lang])

    useEffect(() => {
        if (downloadReportFileResult && downloadReportFileResult?.url) {
            downloadFile(downloadReportFileResult?.url)
            dispatch(clearRedux("GENERAL", { downloadReportFileResult: null }))
        }
    }, [downloadReportFileResult])

    // ═══════════════════════════════════════════════════════════════════════════
    // DATAVIEW HANDLERS & CONFIGURATION
    // Extracted from JSX for readability
    // ═══════════════════════════════════════════════════════════════════════════

    /**
     * Handle condition changes from search/filters/preferences restore
     */
    const handleChangeConditions = (tags, newConditions, total, newColumns, rows, sort, key, fromPreferences, restoreOnly) => {
        let currentConditions = [];
        let currentType = type
        let fltrs = filters;
        let currentDateType = newConditions.find((item) => item?.range?.fields && item?.range?.GTE && item?.range?.LTE)
        let currentOpenSearch = filters.openSearch

        if (currentDateType) {
            const tz = company?.timezone || moment.tz.guess();
            fltrs.startDate = new Date(moment.utc(currentDateType.range.GTE).tz(tz).format("YYYY-MM-DD") + "T00:00:00");
            fltrs.endDate = new Date(moment.utc(currentDateType.range.LTE).tz(tz).format("YYYY-MM-DD") + "T00:00:00");

            const minDate = getRetentionMinDate();
            if (fltrs.startDate < minDate) {
                fltrs.startDate = minDate;
            }

            if (fltrs.startDate > fltrs.endDate) {
                setType('last')
                currentType = 'last'
            } else {
                setFilters({ ...fltrs })
                setType('range')
                currentType = 'range'
            }
        } else {
            setType('last')
            currentType = 'last'
        }

        // Enforce minimum rows in range mode for consistency
        const effectiveRows = currentType === 'last' 
            ? rows 
            : RANGE_MODE_ROWS

        if (effectiveRows)
            setLast({ id: `${effectiveRows}`, name: `${effectiveRows}` })

        let openSearchCondition = newConditions.find((item) => item?.fields && item?.fields[0] === 'reportName')
        if (openSearchCondition) {
            fltrs.openSearch = openSearchCondition?.terms
            currentOpenSearch = openSearchCondition?.terms
            setFilters({ ...fltrs })
            if (!fromPreferences) {
                setOpenSearch(currentOpenSearch)
            }
            toolbarRef?.current?.setNewSearchText(currentOpenSearch)
        }

        // Only call getData when restoring from preferences (initial load).
        // For user-initiated filter changes, applyFilters already handles the fetch.
        if (!fromPreferences) return;

        // restoreOnly: data already persisted in redux, just sync UI states — skip getData
        if (restoreOnly) return;

        currentConditions = currentConditions?.filter((item) => item?.fields[0] !== 'accountId' && item?.fields[0] !== 'reportName');

        if (currentType !== 'last')
            currentConditions.push({
                fields: ["accountId"],
                terms: [company?.id],
                exact: true,
                range: {
                    fields: ["createdOn"],
                    GTE: moment(moment(fltrs.startDate).format("YYYY-MM-DD") + " 00:00:00").utc().format("YYYY-MM-DDTHH:mm:ss.sss") + "Z",
                    LTE: moment(moment(fltrs.endDate).format("YYYY-MM-DD") + " 23:59:00").utc().format("YYYY-MM-DDTHH:mm:ss.sss") + "Z"
                },
                conditionOperator: "AND"
            });
        else
            currentConditions.push({
                fields: ["accountId"],
                terms: [company?.id],
                exact: true,
                range: {
                    fields: ["createdOn"],
                    GTE: moment(getRetentionMinDate()).utc().format("YYYY-MM-DDTHH:mm:ss.sss") + "Z"
                },
                conditionOperator: "AND",
            });


        if (fltrs.openSearch?.length) {
            currentConditions.push({
                fields: ["reportName", "reportType"],
                terms: fltrs.openSearch,
                exact: false,
                conditionOperator: "OR"
            })
        }
        // FIX: Forward newSort — previously sort was NEVER forwarded to getData in this handler,
        // so preference-restored sort was always lost when conditions changed.
        tableRef?.current?.getData({ 
            newRows: effectiveRows, 
            newConditions: currentConditions, 
            newColumns,
            newSort: sort
        });
    };

    /**
     * Extra filters component - renders custom toolbar and last/range filters
     */
    const renderExtraFilters = () => {
        return (
            <div className="scheduled-report-filters" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
                <div className="scheduled-report-toolbar">
                    <Toolbar
                        onSearch={(value) => {
                            setOpenSearch(value);
                            onChangeOpenSearch(value)
                        }}
                        ref={toolbarRef}
                    />
                </div>
            </div>
        )
    };

    const renderExtraButtons = () => {
        return (
            <div className="scheduled-report-filters" style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'flex-end' }}>
                <Filters
                    onChangeFilters={() => applyFilters()}
                    minDate={getRetentionMinDate()}
                    startDate={startDate}
                    endDate={endDate}
                    setStartDate={setStartDate}
                    setEndDate={setEndDate}
                    setBeforeRangeDate={setBeforeRangeDate}
                    beforeRangeDate={beforeRangeDate}
                    onChange={onChange}
                    onChangeStartEndDate={onChangeStartEndDate}
                    onCloseSelectDate={onCloseSelectDate}
                    last={last}
                    setLast={setLast}
                    type={type}
                    setType={(value) => {
                        setType(value);
                        if (value === 'range') {
                            tableRef?.current?.setRows(RANGE_MODE_ROWS);
                        }
                        applyFilters(value);
                    }}
                />
            </div>
        )
    };

    return (
        <div className="scheduled-reports-tab" style={{ marginTop: 10 }}>
            <DataViewInfiniteScroll
                name="pm-scheduled-reports-tab-v1"
                entity="scheduled-reports"
                ref={tableRef}
                columns={columns}
                infiniteScroll={type !== 'last'}
                showTotalCount={type !== 'last'}
                // Use composite key (id + index) because report logs may share the same id
                keyColumn={(item, index) => `${item?.id}-${index}`}
                graphqlConfig={{
                    function: "searchReportLogs",
                    sortType: "ReportLogSortInput",
                    conditionsType: "ReportLogSearchInput",
                    customQuery: "id",
                    dynamicQuery: true,
                }}
                searchConfig={{
                    disabled: true,
                    noTags: true,
                    fields: ["reportName", "reportType"],
                }}
                uiConfig={{
                    hideFilters: true,
                    hideSplit: true,
                    hidePagination: type === "last",
                    topHeight: props?.single ? 286 : 200,
                    height: height && height + "px" || "500px",
                }}
                exportConfig={{
                    fileName: "scheduledreports",
                    noPDF: true,
                    noExcel: true,
                }}
                selectionConfig={{
                    disabled: true,
                }}
                defaultsConfig={{
                    sort: { field: "createdOn", order: "DESC" },
                    rowsPerPage: 50,
                }}
                tableButtons={["export", 'orderColumn']}
                onChangeColumns={(items) => setColumns(items)}
                onChangeConditions={handleChangeConditions}
                extraFilters={renderExtraFilters}
                extraButton={renderExtraButtons}
            />
            {openModal && <Details />}
            {contactsOpenModal && <Contacts />}
        </div>
    )
}

export default ScheduleReports;